/**
 * ResourceLoadSection - Sezione dashboard con carico risorse.
 *
 * Mostra tutti i personale attivi con:
 * - Giorni in corso (commesse IN_ESECUZIONE, icona orologio verde)
 * - Giorni previsti (commesse in pianificazione/approvazione, icona calendario blu)
 * - Personale senza impegni raggruppato e collassabile in fondo
 */

import { useState, useEffect } from 'react'
import { Loader2, Users, ChevronDown, ChevronUp, Clock, Calendar } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import api from '../../core/services/api'

interface FaseCarico {
  commessa_id: string
  commessa_numero: string
  commessa_titolo: string
  fase_id: string
  fase_titolo: string
  giorni: number
  tipo: 'in_corso' | 'prevista'
}

interface PersonaleCarico {
  personale_id: string
  nome: string
  fasi: FaseCarico[]
  giorni_totali_rimanenti: number
  giorni_totali_previsti: number
}

function LoadBar({ inCorso, previsti, max }: { inCorso: number; previsti: number; max: number }) {
  const pctInCorso = max > 0 ? Math.min(100, (inCorso / max) * 100) : 0
  const pctPrevisti = max > 0 ? Math.min(100 - pctInCorso, (previsti / max) * 100) : 0
  const totPct = pctInCorso + pctPrevisti
  const colorInCorso = totPct > 80 ? 'bg-red-400' : totPct > 50 ? 'bg-amber-400' : 'bg-green-400'
  const total = inCorso + previsti

  if (total === 0) {
    return (
      <div className="flex items-center gap-2">
        <div className="flex-1 h-1.5 bg-gray-100 rounded-full" />
        <span className="text-xs text-gray-300 w-10 text-right">–</span>
      </div>
    )
  }

  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden flex">
        <div className={`h-full transition-all ${colorInCorso}`} style={{ width: `${pctInCorso}%` }} />
        <div className="h-full bg-blue-300 transition-all" style={{ width: `${pctPrevisti}%` }} />
      </div>
      <span className="text-xs font-medium text-gray-600 w-10 text-right">{total}g</span>
    </div>
  )
}

export default function ResourceLoadSection() {
  const navigate = useNavigate()
  const [personale, setPersonale] = useState<PersonaleCarico[]>([])
  const [loading, setLoading] = useState(true)
  const [expanded, setExpanded] = useState<Set<string>>(new Set())
  const [mostraSenzaImpegni, setMostraSenzaImpegni] = useState(false)

  useEffect(() => {
    api.get('/v1/commesse/resource-load')
      .then(res => setPersonale(res.data))
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [])

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
      </div>
    )
  }

  if (personale.length === 0) return null

  const maxGiorni = Math.max(...personale.map(p => p.giorni_totali_rimanenti + p.giorni_totali_previsti), 1)
  const conImpegni = personale.filter(p => p.giorni_totali_rimanenti + p.giorni_totali_previsti > 0)
  const senzaImpegni = personale.filter(p => p.giorni_totali_rimanenti + p.giorni_totali_previsti === 0)

  const toggleExpand = (id: string) => {
    setExpanded(prev => {
      const next = new Set(prev)
      next.has(id) ? next.delete(id) : next.add(id)
      return next
    })
  }

  const renderPersona = (persona: PersonaleCarico, idx: number) => {
    const isExp = expanded.has(persona.personale_id)
    const totale = persona.giorni_totali_rimanenti + persona.giorni_totali_previsti
    const vuoto = totale === 0

    return (
      <div key={persona.personale_id} className={idx > 0 ? 'border-t border-gray-100' : ''}>
        {/* Row persona */}
        <div
          className={`flex items-center gap-3 px-4 py-3 transition-colors ${vuoto ? 'opacity-50' : 'hover:bg-gray-50 cursor-pointer'}`}
          onClick={() => !vuoto && toggleExpand(persona.personale_id)}
        >
          {/* Avatar */}
          <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${vuoto ? 'bg-gray-100' : 'bg-primary-100'}`}>
            <span className={`text-xs font-bold ${vuoto ? 'text-gray-400' : 'text-primary-700'}`}>
              {persona.nome.split(' ').map(n => n[0]).slice(0, 2).join('')}
            </span>
          </div>

          {/* Nome + riepilogo */}
          <div className="min-w-0 flex-1">
            <p className={`text-sm font-medium ${vuoto ? 'text-gray-400' : 'text-gray-900'}`}>{persona.nome}</p>
            {vuoto ? (
              <p className="text-xs text-gray-300">Nessun impegno attivo</p>
            ) : (() => {
              const primaFase = persona.fasi[0]
              return (
                <div>
                  {primaFase && (
                    <p className="text-xs text-gray-500 truncate">
                      <span className="font-mono font-bold text-primary-600">{primaFase.commessa_numero}</span>
                      {' '}<span className="truncate">{primaFase.commessa_titolo}</span>
                    </p>
                  )}
                  <p className="text-xs text-gray-400 flex items-center gap-1 flex-wrap">
                    {persona.giorni_totali_rimanenti > 0 && (
                      <span className="text-green-600 font-medium flex items-center gap-0.5">
                        <Clock className="w-2.5 h-2.5" />{persona.giorni_totali_rimanenti}g
                      </span>
                    )}
                    {persona.giorni_totali_previsti > 0 && (
                      <span className="text-blue-500 font-medium flex items-center gap-0.5">
                        <Calendar className="w-2.5 h-2.5" />{persona.giorni_totali_previsti}g
                      </span>
                    )}
                    <span>· {persona.fasi.length} task</span>
                  </p>
                </div>
              )
            })()}
          </div>

          {/* Barra carico */}
          {!vuoto && (
            <div className="w-36 flex-shrink-0">
              <LoadBar inCorso={persona.giorni_totali_rimanenti} previsti={persona.giorni_totali_previsti} max={maxGiorni} />
            </div>
          )}

          {/* Chevron */}
          {!vuoto && (
            <div className="flex-shrink-0 ml-1">
              {isExp
                ? <ChevronUp className="w-4 h-4 text-gray-300" />
                : <ChevronDown className="w-4 h-4 text-gray-300" />}
            </div>
          )}
        </div>

        {/* Fasi espanse */}
        {isExp && (
          <div className="bg-gray-50 border-t border-gray-100 divide-y divide-gray-100">
            {persona.fasi.map(fase => (
              <div
                key={`${fase.fase_id}-${fase.tipo}`}
                className="flex items-center gap-3 px-4 py-2 hover:bg-gray-100 cursor-pointer"
                onClick={() => navigate(`/app/commesse/${fase.commessa_id}`)}
              >
                {fase.tipo === 'in_corso'
                  ? <Clock className="w-3.5 h-3.5 text-green-500 flex-shrink-0" />
                  : <Calendar className="w-3.5 h-3.5 text-blue-400 flex-shrink-0" />
                }
                <span className="font-mono text-xs font-bold flex-shrink-0 text-primary-600">
                  {fase.commessa_numero}
                </span>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium text-gray-700 truncate">{fase.commessa_titolo}</p>
                  <p className={`text-xs truncate ${fase.tipo === 'prevista' ? 'text-blue-400 italic' : 'text-gray-400'}`}>
                    {fase.fase_titolo}{fase.tipo === 'prevista' ? ' · pianificata' : ''}
                  </p>
                </div>
                <span className={`text-xs font-medium flex-shrink-0 ${fase.tipo === 'prevista' ? 'text-blue-400' : 'text-gray-500'}`}>
                  {fase.giorni}g
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    )
  }

  return (
    <div>
      <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
        <Users className="w-5 h-5 text-primary-600" />
        Carico Risorse
        <span className="ml-auto flex items-center gap-3 text-xs font-normal text-gray-400">
          <span className="flex items-center gap-1"><Clock className="w-3 h-3 text-green-500" />In corso</span>
          <span className="flex items-center gap-1"><Calendar className="w-3 h-3 text-blue-400" />Pianificato</span>
        </span>
      </h2>

      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        {conImpegni.map((persona, idx) => renderPersona(persona, idx))}

        {senzaImpegni.length > 0 && (
          <>
            <div
              className={`px-4 py-2 bg-gray-50 flex items-center justify-between cursor-pointer hover:bg-gray-100 transition-colors ${conImpegni.length > 0 ? 'border-t border-gray-200' : ''}`}
              onClick={() => setMostraSenzaImpegni(v => !v)}
            >
              <span className="text-xs text-gray-400 font-medium">
                {senzaImpegni.length} {senzaImpegni.length === 1 ? 'persona' : 'persone'} senza impegni attivi
              </span>
              {mostraSenzaImpegni
                ? <ChevronUp className="w-3.5 h-3.5 text-gray-300" />
                : <ChevronDown className="w-3.5 h-3.5 text-gray-300" />}
            </div>
            {mostraSenzaImpegni && senzaImpegni.map((persona, idx) => renderPersona(persona, idx))}
          </>
        )}
      </div>
    </div>
  )
}
